import { API } from '..';
import { requireTotpParams } from '../../utils/admin-stepup';

export interface Publisher {
  id: string;
  userId: string;
  status: string; // None | Approved | Rejected
  name: string;
  websiteUrl: string;
  domain: string;
  category?: string | null;
  revSharePercent: number;
  isBusiness?: boolean;
  publisherKey?: string | null;
  isDomainVerified: boolean;
  domainVerifyToken?: string | null;
  verifyMetaTag?: string | null;
  adTagSnippet?: string | null;
  rejectReason?: string | null;
  taxLegalName?: string | null;
  taxCode?: string | null;
  taxIdNumber?: string | null;
  taxAddress?: string | null;
}

export interface WithholdingCert {
  payerName: string; payerTaxCode: string; payerAddress: string;
  isBusiness: boolean;
  recipientName: string; recipientTaxCode?: string | null; recipientIdNumber?: string | null; recipientAddress?: string | null;
  websiteName: string; domain: string;
  certNo: string; paidAt: string; countedUpTo: string;
  grossVnd: number; taxPercent: number; taxWithheldVnd: number; netVnd: number;
  hasWithholding: boolean; legalNote: string;
}

export interface PublisherEarnings {
  publisherId: string;
  revSharePercent: number;
  isBusiness: boolean;
  totalEarnedCa: number;
  availableCa: number;
  heldCa: number;
  paidCa: number;
  totalImpressions: number;
  totalClicks: number;
  holdDays: number;
  minPayoutCa: number;
}

export interface PublisherDailyPoint {
  date: string;
  earnedCa: number;
  impressions: number;
  clicks: number;
}
export interface PublisherEarningsStats {
  totalEarnedCa: number;
  totalImpressions: number;
  totalClicks: number;
  daily: PublisherDailyPoint[];
}

export const getMyPublisher = () =>
  API.get<Publisher>('/v1/payment-service/publisher/me').then((r) => r.data);

export const applyPublisher = (payload: { name: string; websiteUrl: string; category?: string; note?: string }) =>
  API.post<Publisher>('/v1/payment-service/publisher/apply', payload).then((r) => r.data);

export const verifyPublisherDomain = () =>
  API.post<{ verified: boolean; message: string }>('/v1/payment-service/publisher/verify-domain').then((r) => r.data);

export const getPublisherEarnings = () =>
  API.get<PublisherEarnings>('/v1/payment-service/publisher/earnings').then((r) => r.data);

export const getPublisherEarningsStats = (days = 14) =>
  API.get<PublisherEarningsStats>(`/v1/payment-service/publisher/earnings-stats?days=${days}`).then((r) => r.data);

export const savePublisherTaxProfile = (p: {
  isBusiness: boolean; taxLegalName?: string; taxCode?: string; taxIdNumber?: string; taxAddress?: string;
}) => API.post<Publisher>('/v1/payment-service/publisher/tax-profile', p).then((r) => r.data);

export const getMyPayouts = () =>
  API.get<PublisherPayout[]>('/v1/payment-service/publisher/payouts').then((r) => r.data);

export const getMyWithholdingCert = (payoutId: string) =>
  API.get<WithholdingCert>(`/v1/payment-service/publisher/payouts/${payoutId}/withholding-cert`).then((r) => r.data);

// ── Admin ──
export interface PublisherPayout {
  id: string;
  amountCa: number;
  taxWithheldCa: number;
  netCa: number;
  countedUpTo: string;
  note?: string | null;
  createdAt: string;
}

export const adminListPublishers = (status = 'Approved') =>
  API.get<Publisher[]>(`/v1/payment-service/admin/publishers?status=${status}`).then((r) => r.data);

export const adminReviewPublisher = (
  publisherId: string,
  approve: boolean,
  rejectReason?: string,
  revSharePercent?: number
) =>
  API.post<Publisher>(`/v1/payment-service/admin/publishers/${publisherId}/review`, {
    approve,
    rejectReason,
    revSharePercent,
  }).then((r) => r.data);

export const adminGetPublisherEarnings = (publisherId: string) =>
  API.get<PublisherEarnings>(`/v1/payment-service/admin/publishers/${publisherId}/earnings`).then((r) => r.data);

// Máy chủ bắt mã Authenticator khi chi trả publisher — hỏi mã ngay tại đây.
export const adminCreatePublisherPayout = async (publisherId: string, note?: string) => {
  const params = requireTotpParams('chi trả publisher');
  return API.post<PublisherPayout>(`/v1/payment-service/admin/publishers/${publisherId}/payout`, { note }, { params }).then((r) => r.data);
};

export const adminListPublisherPayouts = (publisherId: string) =>
  API.get<PublisherPayout[]>(`/v1/payment-service/admin/publishers/${publisherId}/payouts`).then((r) => r.data);

export const adminExportPublisherPayouts = (from?: string, to?: string) => {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  return API.get(`/v1/payment-service/admin/publishers/payouts/export?${q.toString()}`, { responseType: 'blob' }).then((r) => r.data as Blob);
};
